import { Icon } from '@iconify/react';
import type { FC } from 'react';

import type { SalaryPeriod } from '@/hooks/JobBoardFilters/useJobBoardParams';
import type { Currency } from '@/services/external-offers';

import Text from '../Text';

type CurrencySymbolPosition = 'beforeAmount' | 'afterAmount';

const CURRENCY_FORMATS: Record<
  Currency,
  { symbol: string; symbolPosition: CurrencySymbolPosition }
> = {
  USD: { symbol: '$', symbolPosition: 'beforeAmount' },
  EUR: { symbol: '€', symbolPosition: 'beforeAmount' },
  GBP: { symbol: '£', symbolPosition: 'beforeAmount' },
  PLN: { symbol: 'zł', symbolPosition: 'afterAmount' },
  CZK: { symbol: 'Kč', symbolPosition: 'afterAmount' },
};

export const formatSalaryInThousands = (value: number, currency: Currency) => {
  const amountLabel = `${Math.round(value / 1000)}K`;

  const { symbol, symbolPosition } = CURRENCY_FORMATS[currency];

  return symbolPosition === 'afterAmount'
    ? `${amountLabel} ${symbol}`
    : `${symbol}${amountLabel}`;
};

// Job postings quote salary in different periods (hourly/monthly/yearly); we always
// want to show candidates a single, comparable annual figure across the job board.
const ANNUAL_MULTIPLIER_BY_PERIOD: Record<SalaryPeriod, number> = {
  DAY: 260, // ~5 working days/week * 52 weeks
  HOUR: 2080, // ~40 hours/week * 52 weeks
  MONTH: 12,
  YEAR: 1,
};

export const toAnnualSalary = (
  value: number,
  salaryPeriod: SalaryPeriod | null
) => Math.round(value * ANNUAL_MULTIPLIER_BY_PERIOD[salaryPeriod ?? 'YEAR']);

type Props = {
  salaryUpper?: number | null;
  salaryLower?: number | null;
  salaryPeriod: SalaryPeriod | null;
  currency?: Currency;
  format?: 'full' | 'k';
};

const OfferSalary: FC<Props> = ({
  salaryLower,
  salaryPeriod,
  salaryUpper,
  currency,
  format = 'full',
}) => {
  if (format === 'k') {
    const salaryText =
      salaryLower && salaryUpper && currency
        ? `${formatSalaryInThousands(
            toAnnualSalary(salaryLower, salaryPeriod),
            currency
          )} - ${formatSalaryInThousands(
            toAnnualSalary(salaryUpper, salaryPeriod),
            currency
          )}/yr`
        : 'N/A';
    return (
      <div className="flex min-w-0 items-center gap-1">
        <Icon
          className="shrink-0 text-lg text-jb-primary-400"
          icon="material-symbols-light:savings-outline"
        />
        <Text
          variant="jb-body-medium"
          className="truncate font-semibold text-jb-primary-400"
        >
          {salaryText}
        </Text>
      </div>
    );
  }

  if (!salaryUpper || !salaryLower) return null;
  const annualLower = toAnnualSalary(salaryLower, salaryPeriod);
  const annualUpper = toAnnualSalary(salaryUpper, salaryPeriod);
  return (
    <div className="flex items-center gap-1 text-[#7040FF]">
      <Icon
        className="text-body"
        icon="material-symbols-light:savings-outline"
      />
      <Text
        title={`$${annualLower.toLocaleString(
          'en-Us'
        )} - $${annualUpper.toLocaleString('en-Us')}/yr`}
        variant="jb-body-small"
        className="truncate whitespace-nowrap  font-semibold"
      >
        {annualLower.toLocaleString('en-Us')}
        {' - '}
        {annualUpper.toLocaleString('en-Us')}{' '}
        {[currency, 'yr'].filter((e) => e).join('/')}
      </Text>
    </div>
  );
};

export default OfferSalary;
